import { api } from '../api';
import type { Recipe, RecipeIngredient } from '@/types/recipe';
import type { PaginatedResponse } from '@/types/common';
import type { DashboardRecipesStats } from '@/types/company';

export interface RecipeIngredientsQuery {
  page?: number;
  limit?: number;
  sortBy?: 'sortOrder' | 'groupName' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  search?: string;
}

export const recipeApi = {
  getAll: (params?: Record<string, string | number | boolean | null | undefined>) => {
    const sp = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) sp.append(key, String(value));
      });
    }
    const qs = sp.toString();
    return api.get<PaginatedResponse<Recipe>>(`/recipes${qs ? `?${qs}` : ''}`);
  },
  getById: (id: number | string, include?: string[]) => {
    const query = new URLSearchParams();
    include?.forEach((v) => query.append('include', v));
    const qs = query.toString();
    return api.get<Recipe>(`/recipes/${id}${qs ? `?${qs}` : ''}`);
  },
  create: (data: Partial<Recipe>) => api.post<Recipe>('/recipes', data),
  update: (id: number | string, data: Partial<Recipe>) => api.patch<Recipe>(`/recipes/${id}`, data),
  delete: (id: number | string) => api.delete(`/recipes/${id}`),
  getStats: () => api.get<DashboardRecipesStats>(`/recipes/stats`),
  getScopedStats: (filters: { organizationId?: number; companyId?: number }) => {
    const params = new URLSearchParams();
    if (filters?.organizationId !== undefined) params.set('organizationId', String(filters.organizationId));
    if (filters?.companyId !== undefined) params.set('companyId', String(filters.companyId));
    const query = params.toString();
    return api.get<DashboardRecipesStats>(`/recipes/stats${query ? `?${query}` : ''}`);
  },
  
  // Recipe Ingredients
  getIngredients: (recipeId: number | string, params?: RecipeIngredientsQuery) => {
    const sp = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) sp.append(key, String(value));
      });
    }
    const qs = sp.toString();
    return api.get<PaginatedResponse<RecipeIngredient>>(`/recipes/${recipeId}/ingredients${qs ? `?${qs}` : ''}`);
  },
  addIngredient: (recipeId: number | string, data: Record<string, unknown>) =>
    api.post<RecipeIngredient>(`/recipes/${recipeId}/ingredients`, data),
  replaceIngredients: (recipeId: number | string, ingredients: Record<string, unknown>[]) =>
    api.put(`/recipes/${recipeId}/ingredients`, { ingredients }),
  removeIngredient: (recipeId: number | string, ingredientId: number | string) => 
    api.delete(`/recipes/${recipeId}/ingredients/${ingredientId}`),
};
